import { useState, type MouseEvent, type ComponentProps, type ReactNode } from "react";
import classnames from "classnames";

import { useId } from "util/html";

import TooltipOverlay from "./overlay";
import Styles from "./index.module.scss";

type OverlayProps = ComponentProps<typeof TooltipOverlay>;
type Props = {
  /** className for the tooltip popover itself */
  className?: string;
  /** positions the tooltip, default of `top` */
  placement?: OverlayProps["placement"];
  /** node that recieves the tooltip annotation */
  target: ReactNode;
  /** contents of the tooltip */
  children: ReactNode;
  /** aria-label if the trigger doesn't contain a clear label */
  triggerButtonLabel?: string;
  /** interaction that triggers the tooltip's visibility, default of `hover` */
  trigger?: OverlayProps["trigger"];
  /** sets the size of the tooltip overlay, default of `default` */
  size?: OverlayProps["size"];
  /** click handler for the target button */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** props to pass directly to button element */
  triggerButtonProps?: Omit<React.HTMLProps<HTMLButtonElement>, "type">;
  /** Additional className for the tooltip + target container element */
  containerClassName?: string;
  /** Additional className for the target element */
  targetClassName?: string;
};

/**
 * The Tooltip component is used to reveal additional information
 * such as a label or more information "hints" in the UI on either hover
 * or click.
 *
 * It is intended for use on STATIC targets such as icons, images, text etc.
 * If you need a tooltip on an interactive target (ex: button, link etc), refer to
 * the component itself to render a tooltip alongside it.
 */
function Tooltip({
  className,
  target,
  children,
  placement = "top",
  trigger = "hover",
  size = "default",
  triggerButtonLabel,
  onClick,
  triggerButtonProps = {},
  containerClassName,
  targetClassName,
}: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const tooltipDescribedBy = useId();
  const isClickTrigger = trigger === "click";
  return (
    <div className={classnames(Styles.container, containerClassName)}>
      <button
        type="button"
        className={classnames(
          Styles.target,
          !isClickTrigger && Styles.targetHover,
          targetClassName,
        )}
        onClick={(event) => {
          if (isClickTrigger) {
            setIsVisible(!isVisible);
          }
          onClick?.(event);
        }}
        aria-label={triggerButtonLabel}
        onBlur={isClickTrigger ? () => setIsVisible(false) : undefined}
        aria-describedby={isClickTrigger ? undefined : tooltipDescribedBy}
        {...(isClickTrigger && {
          onKeyDown: ({ key }) => {
            if (key === "Escape") {
              setIsVisible(false);
            }
          },
        })}
        {...triggerButtonProps}
      >
        {target}
      </button>
      {(isVisible || trigger === "hover") && (
        <TooltipOverlay
          id={tooltipDescribedBy}
          trigger={trigger}
          className={className}
          placement={placement}
          size={size}
        >
          {children}
        </TooltipOverlay>
      )}
    </div>
  );
}

export default Tooltip;
